<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <!-- Title -->
                <p v-if="baseProduct && baseProduct.titles"><b>{{ $t('productIntro') }}</b> {{baseProduct.titles[currentLanguage.key]}}</p>
                <!-- Language dropdown -->
                <LanguageDropdown class="align-self-end" v-model="currentLanguage"></LanguageDropdown>
                <!-- Gallery, name, highlight and price -->
                <div class="main-content d-flex align-items-center">
                    <!-- Gallery -->
                    <GalleryPicker class="mb-3 mb-md-0 mr-md-5 pb-3" v-model="product.images" :ratioWidth="1" :ratioHeight="1" :is-valid="genericValidator(product.titles) && errorValidator('Titles')"></GalleryPicker>
                    <!-- Name, highlight and price -->
                    <div class="row d-flex flex-wrap justify-content-between flex-grow-1">
                        <!-- Highlight -->
                        <div role="group" class="form-group col-3 col-lg-2 col-xl-1 text-center">
                            <label class=""> {{ $t('highlight') }}</label>
                            <CInputCheckbox :checked="product.highlighted" @update:checked="product.highlighted = !product.highlighted" :invalid-feedback="errorOutput['Highlighted']" :is-valid="errorValidator('Highlighted')"></CInputCheckbox>
                        </div>
                        <!-- Name -->
                        <div role="group" class="form-group flex-grow-1 col-9 col-lg-10 col-xl-11">
                            <label class=""> {{ $t('titleMandatory') }}</label>
                            <CInput type="text" v-model="product.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(product.titles) && errorValidator('Titles')"></CInput>
                        </div>
                        <!-- Categories -->
                        <div role="group" class="form-group col-12">
                            <label class="">{{ $t('categoriesMandatory') }}</label>
                            <OptionsDropdown class="align-self-end" v-model="product.categories" :options="categories" :title="$t('selectCategories')" :is-valid="genericValidator(product.categories) && errorValidator('Categories')" :invalid-feedback="errorOutput['Categories']"></OptionsDropdown>
                        </div>
                        <!-- Price -->
                        <div role="group" class="form-group col-6">
                            <label class=""> {{ $t('taxTypeMandatory') }}</label>
                            <SingleOptionDropdown v-model="product.tax" :options="taxTypes" :title="$t('taxType')" :index="2" :is-valid="genericValidator(product.tax) && errorValidator('TaxType')" :invalid-feedback="errorOutput['TaxType']"></SingleOptionDropdown>
                        </div>
                        <div role="group" class="form-group col-6">
                            <label class=""> {{ $t('priceMandatory') }}</label>
                            <CInput type="number" v-model="product.price" :invalid-feedback="errorOutput['Price']" :is-valid="genericValidator(product.price) && errorValidator('Price')"></CInput>
                        </div>
                    </div>
                </div>
                <!-- Description -->
                <div role="group" class="form-group">
                    <label class=""> {{ $t('descriptionMandatory') }}</label>
                    <vue-editor v-model="product.descriptions[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(product.descriptions) == false || errorValidator('Descriptions') == false }"></vue-editor>
                    <div v-if="errorOutput['Descriptions']" class="invalid-feedback">{{errorOutput['Descriptions']}}</div>
                </div>
                <!-- Action button + loading -->  
                <CSpinner v-if="loading" variant="success" small class="align-self-end"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary col-2 align-self-end" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { deepClone } from "@/helpers/utils.js";
import Product from "@/classes/Product";
import { mapState, mapActions } from 'vuex'
import InputGetList from "@/classes/InputGetList";
import LanguageDropdown from '../controls/LanguageDropdown.vue'
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/views/controls/popups/ConfirmationPopUp.vue";
import router from '@/router/index';
import SingleOptionDropdown from '../controls/SingleOptionDropdown.vue'
import GalleryPicker from '../controls/GalleryPicker.vue'
import { VueEditor } from "vue2-editor";
import OptionsDropdown from '../controls/OptionsDropdown.vue'

const CATEGORY_TYPE = "Product";

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, SingleOptionDropdown, VueEditor, GalleryPicker, OptionsDropdown },
    props:{    
        baseProduct:
        {
            type: Object,
            default: null,
        }
    },
    data()
    {
        return {
            submitted: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            product: new Product({}, {}, []),
            loading: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            peopleTypes: [{label: this.$t("upTo"), key: "UpTo"}, {label: this.$t("atLeast"), key: "AtLeast"}, {label: this.$t("exactly"), key: "Exactly"}],
            taxTypes: [{label: this.$t("free"), key: "Free"}, {label: this.$t("reduced"), key: "Reduced"}, {label: this.$t("intermediate"), key: "Intermediate"}, {label: this.$t("normal"), key: "Normal"}]
        }
    },
    mounted: function()
    {
        this.resetProduct();

        // Get categories
        var input = new InputGetList(999, 1, "", "Name", "id");
        input.type = CATEGORY_TYPE;
        
        this.getList(input); 
    },
    destroyed: function()
    {
        this.reset();
    },
    computed:
    {
        ...mapState('categories', ['listOutput']),
        ...mapState('products', ['processTask']),
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        categoriesInfo: function() {
            return this.listOutput && this.listOutput.data ? this.listOutput.data : null;
        },
        categories: function()
        {
            var result = [];

            if(this.categoriesInfo)
            {
                this.categoriesInfo.forEach(category => 
                {
                    result.push({key: category.id, label: category.name });
                });
            }

            return result;
        },
        errorOutput: function()
        {
            var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

            // Validate Titles and Descriptions
            for (const [key, value] of Object.entries(this.product.titles)) {
                if(!value)
                {
                    result['Titles'] = this.$i18n.t("validateBothTranslations");
                    break;
                };
            }

            for (const [key, value] of Object.entries(this.product.descriptions)) {
                if(!value)
                {
                    result['Descriptions'] = this.$i18n.t("validateBothTranslations");
                    break;
                };
            }

            return result;
        }
    },
    methods:{
        ...mapActions('categories', ['getList']),
        ...mapActions('categories', ['reset']),
        resetProduct(){
            if(!this.baseProduct)
            {
                var titles = {};
                var descriptions = {};

                CONSTANTS.supportedLanguages.forEach(language => { titles[language.key] = ""; descriptions[language.key] = "" });
                this.product = new Product(titles, descriptions, [], []);
            }
            else
            {
                this.product = deepClone(new Product, this.baseProduct);    
                this.product.categories = this.product.categories.map(x=> x.id);  
            }
        },
        submitClicked(){
            this.submitted = true;
            // Emit event
            if(this.product.isValid())
            {
                this.loading = true;
                this.$emit('submit-requested', this.product);
            }
        },
        genericValidator(val){
            var result = undefined; // This is the default, in case the user hasn't submitted yet

            if(this.submitted)
            {
                if(val instanceof Array) // Array (images)
                {
                    result = Boolean(val.length);
                }
                else if(val instanceof Object) // Multilanguage field (title, description)
                {
                    // Validate all fields
                    for (const [key, value] of Object.entries(val)) {
                        if(!value)
                        {
                            result = false;
                            break;
                        };
                    }
                }
                else
                {
                    result = Boolean(val);
                }
            }

            return result;
        },
        errorValidator(fieldName)
        {
            var result = undefined;

            if(this.submitted && Object.keys(this.errorOutput).length)
            {
                result = !Object.keys(this.errorOutput).includes(fieldName);
            }

            return result;
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push('/products/list');
        },
        fileChanged(val)
        {
            this.selectedFile = val;
        },
    },
    watch:
    {
        processTask: function(val)
        {
            if(val != null)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }

                this.loading = false;
            }
        },
        baseProduct: function()
        {
            this.resetProduct();
        },
        alertOutput: function()
        {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

@media only screen and (max-width: 767px) {
    .main-content {
        flex-direction: column;
    }
}

/deep/.quillWrapper.is-invalid .ql-snow.ql-toolbar,
/deep/.quillWrapper.is-invalid .ql-snow.ql-container
{
    border-color: #e55353;
}

/deep/input[type='checkbox']
{
    width: 25px;
    height: 25px;
    cursor: pointer;
}
</style> 